import { Home, Link as LinkIcon, LogOut, Settings, Users } from 'lucide-react';
import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { cn } from '../lib/utils.ts';
import { ThemeToggle } from './ThemeToggle.tsx';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuList,
    NavigationMenuTrigger,
    navigationMenuTriggerStyle
} from './ui/navigation-menu.tsx';

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <Link to="/" className={cn(
                  navigationMenuTriggerStyle(),
                  isActive('/') && "bg-accent text-accent-foreground"
                )}>
                  <Home className="mr-2 h-4 w-4" />
                  Dashboard
                </Link>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <Link to="/urls" className={cn(
                  navigationMenuTriggerStyle(),
                  isActive('/urls') && "bg-accent text-accent-foreground"
                )}>
                  <LinkIcon className="mr-2 h-4 w-4" />
                  My URLs
                </Link>
              </NavigationMenuItem>

              {user?.role === 'admin' && (
                <NavigationMenuItem>
                  <Link to="/users" className={cn(
                    navigationMenuTriggerStyle(),
                    isActive('/users') && "bg-accent text-accent-foreground"
                  )}>
                    <Users className="mr-2 h-4 w-4" />
                    User Management
                  </Link>
                </NavigationMenuItem>
              )}
            </NavigationMenuList>
          </NavigationMenu>

          {/* User menu */}
          <div className="ml-auto flex items-center space-x-4">
            <ThemeToggle />
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger>
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                        {user?.firstName?.charAt(0) || 'U'}
                      </div>
                      <span className="ml-2">{user?.firstName} {user?.lastName}</span>
                    </div>
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-48 p-2">
                      <Link to="/change-password" className="flex w-full items-center p-2 hover:bg-accent rounded-md">
                        <Settings className="mr-2 h-4 w-4" />
                        Change Password
                      </Link>
                      <button
                        onClick={logout}
                        className="flex w-full items-center p-2 hover:bg-accent rounded-md text-destructive"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container py-6">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
